/**
 * SmithChartModal.js
 *
 * Minimal dark panel design - chart is the hero
 * S-param toggles as colored underline text in header
 * Draggable by header row
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import Plot from './Plots/PlotlyChart';
import THEME from '../styles/theme';
import { sParamToSmithData, S_PARAM_COLORS } from '../utils/smithChartUtils';

const API_BASE_URL = '';

const SmithChartModal = ({
  isOpen,
  onClose,
  componentId,
  componentName
}) => {
  const [s2pData, setS2pData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [visibleTraces, setVisibleTraces] = useState({
    S11: true,
    S12: false,
    S21: false,
    S22: false
  });
  const [viewMode, setViewMode] = useState('smith'); // 'smith', 'logmag', or 'both'

  // Dragging state
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Drag handlers
  const handleMouseDown = (e) => {
    if (e.target.closest('.no-drag')) return;
    setIsDragging(true);
    dragStart.current = { x: e.clientX - position.x, y: e.clientY - position.y };
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      setPosition({
        x: e.clientX - dragStart.current.x,
        y: e.clientY - dragStart.current.y
      });
    };

    const handleMouseUp = () => setIsDragging(false);

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  const fetchS2PData = useCallback(async () => {
    const token = localStorage.getItem('gchain_token');
    if (!token) {
      setError('Authentication required');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/components/${componentId}/s2p?full=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch S2P data');
      setS2pData(await response.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [componentId]);

  useEffect(() => {
    if (isOpen && componentId) fetchS2PData();
  }, [isOpen, componentId, fetchS2PData]);

  // Reset position when modal opens
  useEffect(() => {
    if (isOpen) setPosition({ x: 0, y: 0 });
  }, [isOpen]);

  const toggleTrace = (param) => {
    setVisibleTraces(prev => ({ ...prev, [param]: !prev[param] }));
  };

  const buildTraces = () => {
    if (!s2pData?.data) return [];
    const { data } = s2pData;
    const params = [
      { key: 'S11', mag: data.s11_mag_db, phase: data.s11_phase_deg },
      { key: 'S12', mag: data.s12_mag_db, phase: data.s12_phase_deg },
      { key: 'S21', mag: data.s21_mag_db, phase: data.s21_phase_deg },
      { key: 'S22', mag: data.s22_mag_db, phase: data.s22_phase_deg }
    ];
    return params
      .filter(({ key, mag, phase }) => visibleTraces[key] && mag && phase)
      .map(({ key, mag, phase }) => {
        const smithData = sParamToSmithData(mag, phase);
        return {
          type: 'scattersmith',
          real: smithData.real,
          imag: smithData.imag,
          name: key,
          mode: 'lines',
          line: { color: S_PARAM_COLORS[key], width: 2 },
          text: data.frequencies.map(f => `${f.toFixed(1)} MHz`),
          hovertemplate: `${key}<br>%{text}<br>Γ: %{real:.3f} + j%{imag:.3f}<extra></extra>`
        };
      });
  };

  // Build Log Mag traces for scatter plot
  const buildLogMagTraces = () => {
    if (!s2pData?.data) return [];
    const { data } = s2pData;
    const params = [
      { key: 'S11', mag: data.s11_mag_db },
      { key: 'S12', mag: data.s12_mag_db },
      { key: 'S21', mag: data.s21_mag_db },
      { key: 'S22', mag: data.s22_mag_db }
    ];
    return params
      .filter(({ key, mag }) => visibleTraces[key] && mag)
      .map(({ key, mag }) => ({
        type: 'scatter',
        x: data.frequencies,
        y: mag,
        name: key,
        mode: 'lines',
        line: { color: S_PARAM_COLORS[key], width: 2 },
        hovertemplate: `${key}<br>%{x:.1f} MHz<br>%{y:.2f} dB<extra></extra>`
      }));
  };

  // Calculate auto-scaled Y-axis range for Log Mag
  const getLogMagYRange = () => {
    if (!s2pData?.data) return [-50, 10];
    const { data } = s2pData;
    const allValues = [];

    if (visibleTraces.S11 && data.s11_mag_db) allValues.push(...data.s11_mag_db);
    if (visibleTraces.S12 && data.s12_mag_db) allValues.push(...data.s12_mag_db);
    if (visibleTraces.S21 && data.s21_mag_db) allValues.push(...data.s21_mag_db);
    if (visibleTraces.S22 && data.s22_mag_db) allValues.push(...data.s22_mag_db);

    if (allValues.length === 0) return [-50, 10];

    const minVal = Math.min(...allValues);
    const maxVal = Math.max(...allValues);
    const range = maxVal - minVal;
    const padding = range * 0.1;

    // Round to nice values (multiples of 5)
    const yMin = Math.floor((minVal - padding) / 5) * 5;
    const yMax = Math.ceil((maxVal + padding) / 5) * 5;

    return [yMin, yMax];
  };

  const smithLayout = {
    autosize: true,
    margin: { l: 40, r: 40, t: 20, b: 20 },
    paper_bgcolor: 'transparent',
    plot_bgcolor: 'transparent',
    font: {
      color: THEME.textSecondary,
      family: 'Inter, system-ui, sans-serif',
      size: 11
    },
    smith: {
      bgcolor: 'transparent',
      imaginaryaxis: { gridcolor: 'rgba(255,255,255,0.1)', linecolor: 'rgba(255,255,255,0.2)' },
      realaxis: { gridcolor: 'rgba(255,255,255,0.1)', linecolor: 'rgba(255,255,255,0.2)' }
    },
    showlegend: false
  };

  // Get frequency range and tick settings for x-axis
  const getFreqAxisConfig = () => {
    if (!s2pData?.data?.frequencies || s2pData.data.frequencies.length === 0) {
      return { range: [0, 8000], tickmode: 'auto' };
    }
    const freqs = s2pData.data.frequencies;
    const minFreq = freqs[0];
    const maxFreq = freqs[freqs.length - 1];
    const span = maxFreq - minFreq;

    // Calculate a nice tick interval (aim for ~4-5 intermediate ticks)
    const rawStep = span / 5;
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const normalized = rawStep / magnitude;
    let niceStep;
    if (normalized <= 1.5) niceStep = 1 * magnitude;
    else if (normalized <= 3) niceStep = 2 * magnitude;
    else if (normalized <= 7) niceStep = 5 * magnitude;
    else niceStep = 10 * magnitude;

    // Build explicit tick values: start with min, add intermediate ticks, end with max
    const tickvals = [minFreq];
    // Find first "nice" tick after minFreq
    const firstNiceTick = Math.ceil(minFreq / niceStep) * niceStep;
    for (let tick = firstNiceTick; tick < maxFreq; tick += niceStep) {
      if (tick > minFreq) tickvals.push(tick);
    }
    tickvals.push(maxFreq);

    return { range: [minFreq, maxFreq], tickmode: 'array', tickvals };
  };

  const logMagLayout = {
    autosize: true,
    margin: { l: 50, r: 20, t: 20, b: 40 },
    paper_bgcolor: 'transparent',
    plot_bgcolor: 'transparent',
    font: {
      color: THEME.textSecondary,
      family: 'Inter, system-ui, sans-serif',
      size: 11
    },
    xaxis: {
      title: { text: 'Frequency (MHz)', font: { size: 10 } },
      gridcolor: 'rgba(255,255,255,0.1)',
      linecolor: 'rgba(255,255,255,0.2)',
      tickfont: { size: 9 },
      ...getFreqAxisConfig()
    },
    yaxis: {
      title: { text: 'Magnitude (dB)', font: { size: 10 } },
      gridcolor: 'rgba(255,255,255,0.1)',
      linecolor: 'rgba(255,255,255,0.2)',
      tickfont: { size: 9 },
      range: getLogMagYRange()
    },
    showlegend: false
  };

  const plotConfig = {
    displayModeBar: false,
    displaylogo: false
  };

  if (!isOpen) return null;

  const freqRange = s2pData?.data?.frequencies
    ? `${s2pData.data.frequencies[0]?.toFixed(0)}–${s2pData.data.frequencies[s2pData.data.frequencies.length - 1]?.toFixed(0)} MHz`
    : '';

  return createPortal(
    <Overlay onClick={onClose}>
      <Panel
        $wide={viewMode === 'both'}
        onClick={(e) => e.stopPropagation()}
        style={{ transform: `translate(${position.x}px, ${position.y}px)` }}
      >
        <HeaderRow onMouseDown={handleMouseDown}>
          <ComponentLabel>{componentName}</ComponentLabel>
          <SParamToggles className="no-drag">
            {['S11', 'S12', 'S21', 'S22'].map(param => (
              <SParamToggle
                key={param}
                $active={visibleTraces[param]}
                $color={S_PARAM_COLORS[param]}
                onClick={() => toggleTrace(param)}
              >
                {param}
              </SParamToggle>
            ))}
          </SParamToggles>
          {freqRange && <FreqLabel>{freqRange}</FreqLabel>}
          <ViewToggles className="no-drag">
            {['smith', 'logmag', 'both'].map(mode => (
              <ViewToggle
                key={mode}
                $active={viewMode === mode}
                onClick={() => setViewMode(mode)}
              >
                {mode === 'smith' ? 'Smith' : mode === 'logmag' ? 'Log Mag' : 'Both'}
              </ViewToggle>
            ))}
          </ViewToggles>
          <CloseBtn className="no-drag" onClick={onClose}>×</CloseBtn>
        </HeaderRow>

        <ChartArea key={viewMode} $sideBySide={viewMode === 'both'}>
          {loading && <StatusMsg>Loading...</StatusMsg>}
          {error && <StatusMsg $error>Error: {error}</StatusMsg>}
          {!loading && !error && (
            <>
              {(viewMode === 'smith' || viewMode === 'both') && (
                <ChartPane $half={viewMode === 'both'}>
                  {viewMode === 'both' && <ChartLabel>Smith Chart</ChartLabel>}
                  <Plot
                    data={s2pData?.data ? buildTraces() : [{ type: 'scattersmith', real: [], imag: [], mode: 'lines' }]}
                    layout={smithLayout}
                    config={plotConfig}
                    style={{ width: '100%', height: '100%' }}
                    useResizeHandler={true}
                  />
                </ChartPane>
              )}
              {(viewMode === 'logmag' || viewMode === 'both') && (
                <ChartPane $half={viewMode === 'both'}>
                  {viewMode === 'both' && <ChartLabel>Log Magnitude</ChartLabel>}
                  <Plot
                    data={s2pData?.data ? buildLogMagTraces() : [{ type: 'scatter', x: [], y: [], mode: 'lines' }]}
                    layout={logMagLayout}
                    config={plotConfig}
                    style={{ width: '100%', height: '100%' }}
                    useResizeHandler={true}
                  />
                </ChartPane>
              )}
            </>
          )}
        </ChartArea>
      </Panel>
    </Overlay>,
    document.body
  );
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const Panel = styled.div`
  position: relative;
  background: #1e1e1e;
  border-radius: 12px;
  width: ${props => props.$wide ? '1000px' : '500px'} !important;
  height: 500px !important;
  max-height: 500px !important;
  min-height: 500px !important;
  max-width: ${props => props.$wide ? '1000px' : '500px'} !important;
  min-width: ${props => props.$wide ? '1000px' : '500px'} !important;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  overflow: hidden;
  flex-shrink: 0;
  flex-grow: 0;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  background: #252525;
  cursor: move;
  user-select: none;
`;

const ComponentLabel = styled.span`
  color: #fff;
  font-size: 14px;
  font-weight: 600;
`;

const SParamToggles = styled.div`
  display: flex;
  gap: 12px;
  margin-left: 8px;
`;

const SParamToggle = styled.button`
  background: none;
  border: none;
  padding: 2px 0;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  color: ${props => props.$color};
  opacity: ${props => props.$active ? 1 : 0.4};
  border-bottom: 2px solid ${props => props.$active ? props.$color : 'transparent'};
  transition: all 0.15s;

  &:hover {
    opacity: ${props => props.$active ? 1 : 0.7};
  }
`;

const FreqLabel = styled.span`
  color: #666;
  font-size: 11px;
  margin-left: auto;
  margin-right: 8px;
`;

const ViewToggles = styled.div`
  display: flex;
  gap: 4px;
  background: rgba(255,255,255,0.05);
  border-radius: 4px;
  padding: 2px;
`;

const ViewToggle = styled.button`
  background: ${props => props.$active ? 'rgba(255,255,255,0.15)' : 'transparent'};
  border: none;
  padding: 4px 8px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  color: ${props => props.$active ? '#fff' : '#888'};
  border-radius: 3px;
  transition: all 0.15s;

  &:hover {
    background: rgba(255,255,255,0.1);
    color: #fff;
  }
`;

const CloseBtn = styled.button`
  background: transparent;
  border: none;
  color: #666;
  font-size: 20px;
  font-weight: 300;
  cursor: pointer;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.15s;

  &:hover {
    background: rgba(255,255,255,0.1);
    color: #fff;
  }
`;

const ChartArea = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: ${props => props.$sideBySide ? 'row' : 'column'};
  align-items: center;
  justify-content: center;
  min-height: 0;
  overflow: hidden;
  padding: 8px;
  gap: ${props => props.$sideBySide ? '8px' : '0'};
`;

const ChartPane = styled.div`
  flex: 1;
  width: ${props => props.$half ? '50%' : '100%'};
  height: 100%;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const ChartLabel = styled.div`
  color: #888;
  font-size: 10px;
  text-align: center;
  padding: 2px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatusMsg = styled.div`
  color: ${props => props.$error ? '#ff6b6b' : '#666'};
  font-size: 14px;
`;

export default SmithChartModal;
